import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import StageCrewLogo from './StageCrewLogo';
import { Switch } from './ui/switch';

const MotionContext = createContext(null);
const preference = () => { try { return localStorage.getItem('sc_motion') !== 'off'; } catch { return true; } };
const firstVisit = () => { try { return sessionStorage.getItem('sc_intro_seen') !== 'yes'; } catch { return false; } };
export const useStageMotion = () => useContext(MotionContext);

const NavigationEffects = ({ enabled }) => {
  const location = useLocation();
  const motionEnabled = useRef(enabled);
  const modalBackground = useRef(null);
  motionEnabled.current = enabled;
  useEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = previous; };
  }, []);
  useLayoutEffect(() => {
    const background = location.state?.backgroundLocation;
    if (background) {
      if (!modalBackground.current) modalBackground.current = { path: background.pathname, search: background.search, top: window.scrollY };
      return;
    }
    if (modalBackground.current) {
      const saved = modalBackground.current;
      modalBackground.current = null;
      if (location.pathname === saved.path && location.search === saved.search) {
        window.scrollTo({ top: saved.top, left: 0, behavior: 'instant' });
        return;
      }
    }
    const top = () => window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    top();
    const frame = requestAnimationFrame(top);
    const screen = document.querySelector('[data-testid="route-screen"]');
    const animation = motionEnabled.current && screen?.animate ? screen.animate([{ opacity: 0.45 }, { opacity: 1 }], { duration: 300, easing: 'ease-out' }) : null;
    return () => { cancelAnimationFrame(frame); animation?.cancel(); };
  }, [location.key, location.pathname, location.search, location.hash]);
  return null;
};

const ScrollReveals = ({ enabled }) => {
  const location = useLocation();
  useEffect(() => {
    if (!enabled || !window.IntersectionObserver) return;
    const seen = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) target.classList.add('sc-revealed');
        else if (!target.contains(document.activeElement)) target.classList.remove('sc-revealed');
      });
    }, { threshold: 0.06, rootMargin: '0px 0px -12px 0px' });
    const scan = () => {
      document.querySelectorAll('.App .sc-card, .App h1, .App h2, .App [data-testid$="-header"]').forEach((node, i) => {
        if (seen.has(node) || node.closest('[data-no-reveal], [role="dialog"], header, .sc-card')) {
          // The card itself is the reveal target; don't animate its child headings separately.
          if (seen.has(node) || !node.matches('.sc-card') || node.closest('[data-no-reveal], [role="dialog"], header') || node.parentElement?.closest('.sc-card')) return;
        }
        seen.add(node);
        node.classList.add('sc-reveal');
        if (node.getBoundingClientRect().top < window.innerHeight - 12) node.classList.add('sc-revealed');
        node.style.setProperty('--reveal-delay', `${Math.min(i % 4, 3) * 45}ms`);
        observer.observe(node);
      });
    };
    let frame;
    scan();
    const changes = new MutationObserver(() => { cancelAnimationFrame(frame); frame = requestAnimationFrame(scan); });
    changes.observe(document.querySelector('.App'), { subtree: true, childList: true });
    return () => { changes.disconnect(); observer.disconnect(); cancelAnimationFrame(frame); seen.forEach((node) => { node.classList.remove('sc-reveal', 'sc-revealed'); node.style.removeProperty('--reveal-delay'); }); };
  }, [enabled, location.pathname, location.search]);
  return null;
};

export const StageMotionProvider = ({ children }) => {
  const reduced = useReducedMotion();
  const [enabled, setEnabled] = useState(preference);
  const [intro, setIntro] = useState(() => preference() && !window.matchMedia('(prefers-reduced-motion: reduce)').matches && firstVisit() && !window.location.hash.includes('session_id='));
  const skip = useRef(null);
  const effective = enabled && !reduced;
  const dismiss = useCallback(() => setIntro(false), []);
  const playIntro = useCallback((event) => {
    if (event?.metaKey || event?.ctrlKey || event?.shiftKey || event?.altKey) return;
    if (effective) setIntro(true);
  }, [effective]);
  useEffect(() => {
    document.documentElement.dataset.motion = effective ? 'on' : 'off';
    if (!effective) setIntro(false);
  }, [effective]);
  useEffect(() => {
    if (!intro) return;
    try { sessionStorage.setItem('sc_intro_seen', 'yes'); } catch { /* Storage may be disabled. */ }
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    skip.current?.focus();
    const escape = (event) => { if (event.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', escape);
    const timer = setTimeout(dismiss, 2100);
    return () => { clearTimeout(timer); window.removeEventListener('keydown', escape); document.body.style.overflow = overflow; if (previous?.isConnected) previous.focus(); };
  }, [intro, dismiss]);
  const change = (value) => { setEnabled(value); try { localStorage.setItem('sc_motion', value ? 'on' : 'off'); } catch { /* Keep the preference for this visit. */ } };
  return <MotionContext.Provider value={{ enabled, reduced, setEnabled: change, playIntro }}>
    <div inert={intro} aria-hidden={intro || undefined}>{children}</div>
    <NavigationEffects enabled={effective} />
    <ScrollReveals enabled={effective} />
    <AnimatePresence>{intro && <motion.div key="intro" data-testid="stage-intro" data-no-reveal role="dialog" aria-modal="true" aria-labelledby="stage-intro-title" className="stage-intro" initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
      <div className="stage-intro-grid" aria-hidden="true" /><div className="stage-beam stage-beam-left" aria-hidden="true" /><div className="stage-beam stage-beam-right" aria-hidden="true" />
      <div className="stage-intro-center">
        <p data-testid="stage-intro-title" id="stage-intro-title" className="stage-intro-cue">STANDBY / SEÑAL LISTA</p>
        <motion.div data-testid="stage-intro-logo" className="stage-intro-logo" initial={{ opacity: 0, scale: 0.88, y: 15 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}><StageCrewLogo asLink={false} size="xl" /></motion.div>
        <div className="stage-signal-line" aria-hidden="true" /><p className="stage-intro-caption">Detrás de cada directo, un gran equipo.</p>
      </div>
      <button ref={skip} data-testid="stage-intro-skip" onClick={dismiss} className="stage-intro-skip sc-focus">Entrar ahora <span aria-hidden="true">↗</span></button>
    </motion.div>}</AnimatePresence>
  </MotionContext.Provider>;
};

export const MotionSettings = () => {
  const { enabled, reduced, setEnabled } = useStageMotion();
  return <section className="sc-card p-6 md:p-8 mb-6" data-testid="profile-motion-settings">
    <div className="flex items-center gap-4 justify-between"><div><h2 id="motion-settings-title" className="text-base font-semibold text-white">Animaciones y accesibilidad</h2><p className="text-sm text-slate-400 mt-2">Entrada del logo, movimientos al hacer scroll y animaciones de Kiu.</p></div><Switch data-testid="profile-motion-toggle" aria-labelledby="motion-settings-title" checked={enabled} onCheckedChange={setEnabled} /></div>
    <p data-testid="profile-motion-note" className="text-xs text-slate-500 mt-3">Ajuste guardado en este dispositivo. {reduced ? 'Tu sistema tiene activada la reducción de movimiento; se respeta automáticamente.' : 'Puedes desactivarlo para navegar sin movimiento decorativo.'}</p>
  </section>;
};
